import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { requestIdOf } from './AsyncStateBoundary';

/**
 * The ONE failed-query presentation for audit-critical surfaces (spec-13
 * §9 / §10.2): localized failure + retry guidance, the authoritative
 * server request id when the client has it (body detail or the exposed
 * `x-request-id` header), and a retry action. Never renders the raw error
 * message. Lists, pickers, the notification feed, and `AsyncStateBoundary`
 * all use this instead of duplicating (unsafe) error handling.
 */
export const QueryFailureNotice = ({
  error,
  onRetry,
  className,
  compact = false,
}: {
  error?: unknown;
  onRetry?: () => void;
  className?: string;
  /** Inline variant for form fields / dropdown pickers. */
  compact?: boolean;
}) => {
  const { t } = useTranslation(['issues']);
  const requestId = requestIdOf(error);
  return (
    <div
      role="alert"
      className={cn(
        compact
          ? 'space-y-1 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs'
          : 'space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm',
        className,
      )}
    >
      <p className="font-medium text-efs-text-primary">{t('efs.issues.states.failed')}</p>
      {compact ? null : <p className="text-efs-text-muted">{t('efs.issues.states.failed_hint')}</p>}
      {requestId ? (
        <p className="text-xs text-efs-text-muted">
          {t('efs.issues.states.request_id')}: <code>{requestId}</code>
        </p>
      ) : null}
      {onRetry ? (
        <Button size="sm" variant="outline" className="min-h-8" onClick={onRetry}>
          <RefreshCw className="mr-1 h-3.5 w-3.5" aria-hidden />
          {t('efs.issues.states.retry')}
        </Button>
      ) : null}
    </div>
  );
};
